'use client';

import { useState } from 'react';

interface BiomarkerOption {
  id: string;
  label: string;
}

export function ReportErrorForm({ biomarkers }: { biomarkers: BiomarkerOption[] }) {
  const [biomarkerTemplateId, setBiomarker] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [concern, setConcern] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [reporterEmail, setEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [receivedId, setReceivedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/medical/error-report', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          biomarkerTemplateId,
          pageUrl,
          concern,
          sourceUrl,
          reporterEmail,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        received?: boolean;
        id?: string;
        error?: string;
      };
      if (!res.ok || !body.received) {
        throw new Error(body.error ?? `submit returned ${res.status}`);
      }
      setReceivedId(body.id ?? 'unknown');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (receivedId) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-5 py-4">
        <p className="font-semibold">Thank you.</p>
        <p className="mt-1 text-sm">
          Your report has been received (ID: <code className="font-mono">{receivedId}</code>). Our medical
          review team will respond to <span className="font-medium">{reporterEmail}</span> within 5 business days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="Which biomarker does this concern? (optional)">
        <select
          value={biomarkerTemplateId}
          onChange={(e) => setBiomarker(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ff9100]"
        >
          <option value="">— page-level issue / not a specific marker —</option>
          {biomarkers.map((b) => (
            <option key={b.id} value={b.id}>{b.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Page URL (optional)">
        <input
          type="url"
          value={pageUrl}
          onChange={(e) => setPageUrl(e.target.value)}
          placeholder="https://feelgood.health/…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ff9100]"
        />
      </Field>

      <Field label="What do you believe is incorrect?" required>
        <textarea
          value={concern}
          onChange={(e) => setConcern(e.target.value)}
          rows={5}
          required
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ff9100]"
        />
      </Field>

      <Field label="Supporting source URL (optional)">
        <input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ff9100]"
        />
      </Field>

      <Field label="Your email (for follow-up)" required>
        <input
          type="email"
          value={reporterEmail}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ff9100]"
        />
      </Field>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || concern.length < 10 || !reporterEmail}
        className="inline-flex items-center rounded-xl bg-cta-gradient text-white text-sm font-semibold uppercase tracking-[0.15em] px-6 py-3 shadow-sm hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 transition-transform"
      >
        {submitting ? 'Submitting…' : 'Submit report'}
      </button>
    </form>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#0f2534] mb-1">
        {label}
        {required && <span className="text-[#ff9100] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
